import { Text, Input, Button, Flex, Form, Label, TextArea } from '@pega/cosmos-react-core';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useLocalTranslation from '../../../hooks/useLocalTranslation';
import { appState } from '../../../interfaces/common';
import { updateBannerInfo, updateLoaderDetails } from '../../../redux/actions';
import { addNewVonageAccount } from './VonageApis';

interface VonageFormData {
  applicationId: string;
  privateKey: string;
  name: string;
  phoneNumber: string;
}

interface VonageProps {
  type: string;
}

const CreateVonage: React.FC<VonageProps> = (props) => {
  const { register, handleSubmit, errors, reset, formState } = useForm<VonageFormData>({ mode: 'all' });
  const sessionId = useSelector((state: appState) => state.sessionId);
  const dispatch = useDispatch();
  const translate = useLocalTranslation();
  const navigate = useNavigate();
  const endpoint = props.type === 'sms' ? 'vonage-sms' : 'vonage-whatsapp';

  const onSubmit = async (data: VonageFormData) => {
    const encryptedPrivateKey = btoa(data.privateKey);

    const payload = {
      application_id: data.applicationId,
      private_key: encryptedPrivateKey,
      name: data.name,
      phone_number: data.phoneNumber,
    };
    if (formState.touched) {
      dispatch(updateLoaderDetails(true));
      const accountType = props.type;
      const json = await addNewVonageAccount(sessionId, payload, accountType);
      dispatch(updateLoaderDetails(false));
      if (json?.status === 200) {
        reset();
        navigate(`/${endpoint}/${json?.data?.accountId}`);
        dispatch(updateBannerInfo(`Vonage ${props.type} ${translate('account_registered')}`, 'success'));
      } else if (json?.status === 403) {
        navigate('/');
        dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
      } else {
        let errorMessage;
        if (json?.data?.code) {
          errorMessage = translate(json.data.code);
        }
        const failureMessage =
          props.type === 'sms' ? 'vonage_sms_creation_failure' : 'vonage_whatsapp_creation_failure';
        dispatch(updateBannerInfo(errorMessage || translate(failureMessage), 'urgent'));
      }
    }
  };

  const actions = (
    <Flex>
      <Button name="Save" type="submit" variant="primary">
        {translate('save')}
      </Button>
    </Flex>
  );

  return (
    <Form actions={actions} className="basic-form" onSubmit={handleSubmit(onSubmit)}>
      <Input
        name="name"
        label={translate('name')}
        required={true}
        autoComplete="off"
        info={errors.name?.message}
        status={errors.name?.message ? 'error' : undefined}
        ref={register({ required: translate('name_empty') })}
      />

      <Input
        name="phoneNumber"
        type="text"
        label={translate('number')}
        autoComplete="off"
        placeholder="+1 555 555 5555"
        info={errors.phoneNumber?.message}
        required={true}
        status={errors.phoneNumber?.message ? 'error' : undefined}
        ref={register({
          required: translate('phone_number_criteria_error'),
          pattern: { value: /^[+]{1}[\s0-9]*$/, message: translate('phone_number_criteria_error_1') },
        })}
      />
      <Input
        name="applicationId"
        label={translate('application_id')}
        autoComplete="off"
        type="text"
        info={errors.applicationId?.message}
        required={true}
        status={errors.applicationId?.message ? 'error' : undefined}
        ref={register({
          required: translate('application_id_criteria_error'),
        })}
      />
      <TextArea
        name="privateKey"
        type="text"
        label={translate('private_key')}
        autoResize={true}
        required={true}
        info={errors.privateKey?.message}
        status={errors.privateKey?.message ? 'error' : undefined}
        ref={register({ required: translate('private_key_token_empty') })}
        style={{ maxHeight: 200 }}
      />

      <Flex
        container={{
          direction: 'column',
          itemGap: 1,
        }}
      >
        <Label>{translate('webhook')}</Label>
        <Text>
          {process.env.REACT_APP_INCOMING_WEBHOOK}/{endpoint}
        </Text>
      </Flex>
    </Form>
  );
};

export default CreateVonage;
